import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import * as FiIcons from 'react-icons/fi';
import Layout from '../components/Layout';
import { fetchUsers } from '../api/User';
import { usePermissions } from '../hooks/usePermissions';
import {
  createMcpCredential,
  fetchMcpAuditEvents,
  fetchMcpCredentials,
  revokeMcpCredential,
  rotateMcpCredential,
} from '../api/McpAdmin';
import { User } from '../types/User';
import { McpAuditEvent, McpCredential } from '../types/Mcp';
import { filterEnabledRegularUsers } from '../utils/userFilters';
import '../css/Pages.css';
import '../css/McpAdminPage.css';

const FiAlertCircle = FiIcons.FiAlertCircle as unknown as React.FC;
const FiCheckCircle = FiIcons.FiCheckCircle as unknown as React.FC;
const FiCopy = FiIcons.FiCopy as unknown as React.FC;
const FiKey = FiIcons.FiKey as unknown as React.FC;
const FiRotateCw = FiIcons.FiRotateCw as unknown as React.FC;
const FiSlash = FiIcons.FiSlash as unknown as React.FC;

const formatDateTime = (value?: string) => {
  if (!value) return 'Not available';
  const timestamp = new Date(value);
  if (Number.isNaN(timestamp.getTime())) return value;
  return timestamp.toLocaleString();
};

const formatStatusLabel = (value?: string) => {
  if (!value) return 'Unknown';
  return value
    .split('_')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
};

const getDisplayedSecret = (secret?: { token?: string; secret?: string; apiKey?: string }) =>
  secret?.token || secret?.secret || secret?.apiKey || '';

const McpAdminPage: React.FC = () => {
  const permissions = usePermissions();
  const canManageMcpAdmin = permissions.canManageMcpAdmin;
  const [credentials, setCredentials] = useState<McpCredential[]>([]);
  const [auditEvents, setAuditEvents] = useState<McpAuditEvent[]>([]);
  const [loadingCredentials, setLoadingCredentials] = useState(false);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [submittingCredential, setSubmittingCredential] = useState(false);
  const [actionCredentialId, setActionCredentialId] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [toolFilter, setToolFilter] = useState('');
  const [credentialName, setCredentialName] = useState('');
  const [credentialDescription, setCredentialDescription] = useState('');
  const [latestSecret, setLatestSecret] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const data = await fetchUsers();
      setUsers(filterEnabledRegularUsers(Array.isArray(data) ? data : []));
    } catch (error: any) {
      setUsers([]);
      toast.error(error?.message || 'Failed to load CRM users.');
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const loadCredentials = useCallback(async () => {
    setLoadingCredentials(true);
    try {
      const response = await fetchMcpCredentials();
      setCredentials(Array.isArray(response.items) ? response.items : []);
    } catch (error: any) {
      setCredentials([]);
      toast.error(error?.message || 'Failed to load MCP credentials.');
    } finally {
      setLoadingCredentials(false);
    }
  }, []);

  const loadAuditEvents = useCallback(async () => {
    setLoadingAudit(true);
    try {
      const response = await fetchMcpAuditEvents({ limit: 20 });
      setAuditEvents(Array.isArray(response.items) ? response.items : []);
    } catch (error: any) {
      setAuditEvents([]);
      toast.error(error?.message || 'Failed to load MCP audit activity.');
    } finally {
      setLoadingAudit(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    loadCredentials();
    loadAuditEvents();
  }, [loadAuditEvents, loadCredentials, loadUsers]);

  const selectedUser = useMemo(
    () => users.find((user) => String(user.id) === selectedUserId) || null,
    [selectedUserId, users]
  );

  const filteredAuditEvents = useMemo(() => {
    return auditEvents.filter((event) => {
      if (statusFilter !== 'all' && event.status !== statusFilter) return false;
      if (!toolFilter.trim()) return true;
      return event.toolName.toLowerCase().includes(toolFilter.trim().toLowerCase());
    });
  }, [auditEvents, statusFilter, toolFilter]);

  const handleCreateCredential = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedName = credentialName.trim();
    const resolvedUserId = Number(selectedUserId);
    if (!selectedUserId || !Number.isFinite(resolvedUserId) || resolvedUserId <= 0) {
      toast.error('Select the CRM user this credential will run as.');
      return;
    }
    if (!trimmedName) {
      toast.error('Enter a credential name.');
      return;
    }
    if (!canManageMcpAdmin) {
      toast.error('You do not have permission to issue MCP credentials.');
      return;
    }

    setSubmittingCredential(true);
    try {
      const response = await createMcpCredential({
        user_id: resolvedUserId,
        name: trimmedName,
        description: credentialDescription.trim() || undefined,
      });
      const secret = getDisplayedSecret(response.secret);
      setLatestSecret(secret);
      setSelectedUserId('');
      setCredentialName('');
      setCredentialDescription('');
      toast.success('MCP credential created.');
      await Promise.all([loadCredentials(), loadAuditEvents()]);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to create MCP credential.');
    } finally {
      setSubmittingCredential(false);
    }
  };

  const handleRotateCredential = async (credential: McpCredential) => {
    if (!credential.id) {
      toast.error('Missing credential id.');
      return;
    }
    if (!canManageMcpAdmin) {
      toast.error('You do not have permission to rotate MCP credentials.');
      return;
    }
    if (!window.confirm(`Rotate credential "${credential.name}"? The old secret will stop working.`)) {
      return;
    }

    setActionCredentialId(credential.id);
    try {
      const response = await rotateMcpCredential(credential.id);
      setLatestSecret(getDisplayedSecret(response.secret));
      toast.success('MCP credential rotated.');
      await Promise.all([loadCredentials(), loadAuditEvents()]);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to rotate MCP credential.');
    } finally {
      setActionCredentialId('');
    }
  };

  const handleRevokeCredential = async (credential: McpCredential) => {
    if (!credential.id) {
      toast.error('Missing credential id.');
      return;
    }
    if (!canManageMcpAdmin) {
      toast.error('You do not have permission to revoke MCP credentials.');
      return;
    }
    if (!window.confirm(`Revoke credential "${credential.name}"? This action disables future MCP access.`)) {
      return;
    }

    setActionCredentialId(credential.id);
    try {
      await revokeMcpCredential(credential.id);
      toast.success('MCP credential revoked.');
      await Promise.all([loadCredentials(), loadAuditEvents()]);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to revoke MCP credential.');
    } finally {
      setActionCredentialId('');
    }
  };

  const handleCopySecret = async () => {
    if (!latestSecret) return;
    try {
      await navigator.clipboard.writeText(latestSecret);
      toast.success('Secret copied.');
    } catch {
      toast.error('Failed to copy secret.');
    }
  };

  return (
    <Layout>
      <div className="page-container mcp-admin-page">
        <div className="page-header mcp-admin-header">
          <div>
            <h2>MCP Admin</h2>
            <p className="mcp-admin-subtitle">
              Issue and manage MCP credentials from the CRM backend, and inspect recent MCP tool activity.
            </p>
          </div>
        </div>

        {latestSecret && (
          <div className="mcp-secret-banner" role="status">
            <div className="mcp-secret-banner__content">
              <FiAlertCircle />
              <div>
                <strong>Copy this secret now.</strong>
                <div>The browser only shows the newly issued credential secret once.</div>
              </div>
            </div>
            <div className="mcp-secret-banner__actions">
              <code>{latestSecret}</code>
              <button className="btn-secondary" onClick={handleCopySecret}>
                <FiCopy /> Copy
              </button>
              <button className="btn-secondary" onClick={() => setLatestSecret('')}>
                Dismiss
              </button>
            </div>
          </div>
        )}

        <div className="mcp-admin-grid">
          <section className="card mcp-admin-card">
            <h3>Issue Credential</h3>
            <p className="mcp-admin-hint">
              Create a dedicated credential for an approved MCP client or integration. Do not reuse browser session tokens.
            </p>
            <form className="mcp-admin-form" onSubmit={handleCreateCredential}>
              <label>
                <span>Runs as user</span>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  disabled={loadingUsers || !canManageMcpAdmin}
                  required
                >
                  <option value="">{loadingUsers ? 'Loading users…' : 'Select CRM user'}</option>
                  {users.map((user) => (
                    <option key={user.id} value={String(user.id)}>
                      {user.name} {user.role ? `(${user.role})` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Name</span>
                <input
                  type="text"
                  value={credentialName}
                  onChange={(e) => setCredentialName(e.target.value)}
                  placeholder="Sales laptop MCP"
                />
              </label>
              <label>
                <span>Description</span>
                <textarea
                  value={credentialDescription}
                  onChange={(e) => setCredentialDescription(e.target.value)}
                  placeholder="Optional context for support and audit visibility"
                  rows={3}
                />
              </label>
              {selectedUser && (
                <p className="mcp-admin-hint">
                  This credential will execute with <strong>{selectedUser.name}</strong>
                  {selectedUser.email ? ` (${selectedUser.email})` : ''}.
                </p>
              )}
              <button className="btn" type="submit" disabled={submittingCredential || !canManageMcpAdmin}>
                <FiKey /> {submittingCredential ? 'Issuing…' : 'Issue Credential'}
              </button>
            </form>
            {!canManageMcpAdmin && (
              <p className="mcp-admin-hint">You can view MCP configuration, but only approved admins can issue or rotate credentials.</p>
            )}
          </section>

          <section className="card mcp-admin-card">
            <div className="mcp-admin-card__header">
              <h3>Credentials</h3>
              <span>{credentials.length} total</span>
            </div>
            {loadingCredentials && <div>Loading credentials…</div>}
            {!loadingCredentials && credentials.length === 0 && (
              <div className="mcp-empty-state">No MCP credentials found.</div>
            )}
            {!loadingCredentials && credentials.length > 0 && (
              <div className="mcp-credential-list">
                {credentials.map((credential) => {
                  const isBusy = actionCredentialId === credential.id;
                  return (
                    <article key={credential.id} className="mcp-credential-item">
                      <div className="mcp-credential-item__header">
                        <div>
                          <h4>{credential.name}</h4>
                          {credential.description && <p>{credential.description}</p>}
                        </div>
                        <span className={`mcp-status-badge is-${credential.status}`}>
                          {formatStatusLabel(credential.status)}
                        </span>
                      </div>
                      <dl className="mcp-credential-meta">
                        <div>
                          <dt>Preview</dt>
                          <dd>{credential.tokenPreview || 'Hidden'}</dd>
                        </div>
                        <div>
                          <dt>Created</dt>
                          <dd>{formatDateTime(credential.createdAt)}</dd>
                        </div>
                        <div>
                          <dt>Last used</dt>
                          <dd>{formatDateTime(credential.lastUsedAt)}</dd>
                        </div>
                        <div>
                          <dt>Runs as user</dt>
                          <dd>{credential.ownerUserName || credential.ownerUserId || 'Not available'}</dd>
                        </div>
                        <div>
                          <dt>Issued by</dt>
                          <dd>{credential.issuerUserName || credential.issuerUserId || 'Not available'}</dd>
                        </div>
                      </dl>
                      <div className="mcp-credential-actions">
                        <button
                          className="btn-secondary"
                          onClick={() => handleRotateCredential(credential)}
                          disabled={isBusy || credential.status === 'revoked' || !canManageMcpAdmin}
                        >
                          <FiRotateCw /> {isBusy ? 'Working…' : 'Rotate'}
                        </button>
                        <button
                          className="btn-secondary btn-danger"
                          onClick={() => handleRevokeCredential(credential)}
                          disabled={isBusy || credential.status === 'revoked' || !canManageMcpAdmin}
                        >
                          <FiSlash /> {credential.status === 'revoked' ? 'Revoked' : 'Revoke'}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        <section className="card mcp-admin-card mcp-admin-card--audit">
          <div className="mcp-admin-card__header">
            <div>
              <h3>Recent MCP Activity</h3>
              <p className="mcp-admin-hint">
                Recent tool usage for support and debugging. Filters apply in the browser to the fetched activity set.
              </p>
            </div>
          </div>

          <div className="mcp-audit-filters">
            <label>
              <span>Status</span>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="success">Success</option>
                <option value="error">Error</option>
                <option value="denied">Denied</option>
                <option value="pending">Pending</option>
              </select>
            </label>
            <label>
              <span>Tool name</span>
              <input
                type="text"
                value={toolFilter}
                onChange={(e) => setToolFilter(e.target.value)}
                placeholder="crm.create_query"
              />
            </label>
          </div>

          {loadingAudit && <div>Loading activity…</div>}
          {!loadingAudit && filteredAuditEvents.length === 0 && (
            <div className="mcp-empty-state">No MCP activity matched the current filters.</div>
          )}
          {!loadingAudit && filteredAuditEvents.length > 0 && (
            <div className="mcp-audit-list">
              {filteredAuditEvents.map((event) => (
                <article key={event.id} className="mcp-audit-item">
                  <div className="mcp-audit-item__header">
                    <div className="mcp-audit-tool">
                      {event.status === 'success' ? <FiCheckCircle /> : <FiAlertCircle />}
                      <strong>{event.toolName}</strong>
                    </div>
                    <span className={`mcp-status-badge is-${event.status}`}>
                      {formatStatusLabel(event.status)}
                    </span>
                  </div>
                  <div className="mcp-audit-meta">
                    <span>{event.actingUserName || 'Unknown user'}</span>
                    <span>{formatDateTime(event.timestamp)}</span>
                    {event.entityType && <span>{`${event.entityType}${event.entityId ? ` #${event.entityId}` : ''}`}</span>}
                    {event.requestId && <span>{`Request ${event.requestId}`}</span>}
                  </div>
                  {event.message && <p className="mcp-audit-message">{event.message}</p>}
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
};

export default McpAdminPage;
